import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

const services = [
  {
    id: 1,
  title: 'Dynamics 365',
  short: 'Transform your operations with Dynamics 365. We help you modernize customer engagement, service, sales, and marketing—whether starting fresh or moving from on-premises to cloud.',
  desc: `Dynamics 365 offers a unified platform for Customer Engagement, Sales, Marketing, and Customer Service and many more. 
  We specialize in helping organizations migrate from on-premises deployments to the cloud, ensuring a smooth transition and full use of Microsoft’s latest capabilities.
  Our solutions streamline business processes, enhance team collaboration, and provide insights to drive smarter decisions and stronger customer relationships.`
  },
  {
    id: 2,
    title: 'Power Platform',
    short: 'Automate your business processes with Dataverse, Power Apps, Power Automate, Azure and unlock the power of data with Power BI',
    desc: `The Microsoft Power Platform enables everyone to analyze data, build solutions, automate processes, and create virtual agents. 
    Connect your apps and data, build custom solutions quickly, and drive innovation across your organization. 
    Power Platform is designed for rapid development and seamless integration with Microsoft 365 and Dynamics 365.`
  },
  {
    id: 4,
    title: 'Power Pages',
    short: 'Create secure, low-code business websites quickly with Microsoft Power Pages.',
    desc: `Power Pages lets you rapidly design, configure, and publish modern websites that work across devices. 
    Empower your teams to build sites for customers, partners, and employees—integrated with your data and business logic. 
    Easily customize branding, security, and user experiences with intuitive tools and templates.`
  },
];

const packages = [
  {
    name: 'Support Package',
    description: 'Ongoing technical support and troubleshooting for your Microsoft solutions. Includes priority response and monthly health checks.',
    details: `Our Support Package gives you access to our expert team for troubleshooting, updates, and proactive monitoring. 
    You’ll receive monthly health checks and priority response for any issues, ensuring your systems run smoothly and securely.`
  },
  {
    name: 'Training Package',
    description: 'Hands-on training sessions for your team on Dynamics 365, Power Platform, or Azure. Includes custom materials and Q&A.',
    details: `The Training Package includes interactive workshops, custom training materials, and live Q&A sessions. 
    Empower your staff to get the most out of your Microsoft solutions with practical, scenario-based learning.`
  },
  {
    name: 'Free Assessment Package',
    description: 'A complimentary review of your current systems and needs. Get recommendations and a roadmap for digital transformation.',
    details: `Our Free Assessment Package provides a no-obligation review of your business processes and IT landscape. 
    Receive a tailored report with actionable recommendations and a roadmap for your digital transformation journey.`
  },
];

const Services: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(null);
  const [openPackage, setOpenPackage] = useState<number | null>(null);
  const openService = services.find(s => s.id === openId);
  const openPkg = openPackage !== null ? packages[openPackage] : null;

  return (
    <div className="services-container">
      {/* Service Delivery Tiles */}
      <section className="services-section">
        <h2>Service Delivery</h2>
        <p className="services-intro">
          We understand that every business is unique. These are the services we specialize in to help your business transform and grow with Microsoft technologies.
        </p>
        <div className="services-grid">
          {services.map(service => (
            <div
              key={service.id}
              className="service-tile"
              onClick={() => setOpenId(service.id)}
              tabIndex={0}
              role="button"
              aria-pressed={openId === service.id}
            >
              <h3>{service.title}</h3>
              <p className="service-short">{service.short}</p>
            </div>
          ))}
        </div>
        {openService && (
          <div className="modal-backdrop" onClick={() => setOpenId(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setOpenId(null)}>&times;</button>
              <h3>{openService.title}</h3>
              <p>{openService.desc}</p>
              <Link to="/contact" className="modal-contact-link">
                Contact us for more information
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Add-on Packages */}
      <section className="packages-section">
        <h2>Add-on Packages</h2>
        <p className="packages-intro">
          We understand that your needs may go beyond standard project delivery. That’s why we offer these flexible add-on packages—designed to provide extra value, support, and expertise whenever you need it.
        </p>
        <div className="packages-list">
          {packages.map((pkg, idx) => (
            <div
              className="package-card"
              key={pkg.name}
              tabIndex={0}
              role="button"
              onClick={() => setOpenPackage(idx)}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') setOpenPackage(idx);
              }}
              aria-pressed={openPackage === idx}
            >
              <h4>{pkg.name}</h4>
              <p>{pkg.description}</p>
            </div>
          ))}
        </div>
        {openPkg && (
          <div className="modal-backdrop" onClick={() => setOpenPackage(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setOpenPackage(null)}>&times;</button>
              <h3>{openPkg.name}</h3>
              <p>{openPkg.details}</p>
              <Link to="/contact" className="modal-contact-link">
                Contact us for more information
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Services;
